import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Dropdown, Badge, Button } from 'react-bootstrap';
import moment from 'moment';

const NOMBRES_MESES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const CheckboxAnio = ({ anio, totalMeses, seleccionados, onToggle, disabled }) => {
    const ref = useRef(null);
    const marcado = totalMeses > 0 && seleccionados.length === totalMeses;
    const indeterminado = seleccionados.length > 0 && seleccionados.length < totalMeses;
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminado;
    }, [indeterminado]);
    return (
        <input
            ref={ref}
            type="checkbox"
            className="form-check-input"
            checked={marcado}
            disabled={disabled}
            onChange={(e) => onToggle(anio, e.target.checked)}
        />
    );
};

/**
 * FiltroFechaExcel - Dropdown de filtro multi-fecha estilo Excel (año → mes).
 * Componente totalmente controlado (source of truth = `value`).
 *
 * @param {Object} props
 * @param {(Date|string)[]} props.fechas - Fechas presentes en el dataset
 * @param {{anio: number, meses: number[]}[]} props.value - Selección actual
 * @param {(value: {anio:number, meses:number[]}[]) => void} props.onChange
 * @param {boolean} [props.disabled=false]
 */
const FiltroFechaExcel = ({ fechas = [], value = [], onChange, disabled = false }) => {
    const [aniosExpandido, setAniosExpandido] = useState(() => new Set());

    const mesesPorAnio = useMemo(() => {
        const mapa = new Map();
        fechas.forEach(f => {
            if (!f) return;
            const m = moment(f);
            if (!m.isValid()) return;
            const anio = m.year();
            const mes = m.month();
            if (!mapa.has(anio)) mapa.set(anio, new Set());
            mapa.get(anio).add(mes);
        });
        return mapa;
    }, [fechas]);

    const aniosDisponibles = useMemo(() => {
        return Array.from(mesesPorAnio.keys()).sort((a, b) => b - a);
    }, [mesesPorAnio]);

    const mesesSeleccionadosDeAnio = (anio) =>
        value.find(v => v.anio === anio)?.meses || [];

    const anioTieneSeleccion = (anio) =>
        value.some(v => v.anio === anio && v.meses.length > 0);

    const estaExpandido = (anio) =>
        aniosExpandido.has(anio) || anioTieneSeleccion(anio);

    const aplicarCambioAnio = (anio, nuevosMeses) => {
        const sinEsteAnio = value.filter(v => v.anio !== anio);
        const nuevoValue = nuevosMeses.length > 0
            ? [...sinEsteAnio, { anio, meses: nuevosMeses }]
            : sinEsteAnio;
        onChange(nuevoValue);
    };

    const toggleMes = (anio, mes) => {
        const actuales = mesesSeleccionadosDeAnio(anio);
        const nuevosMeses = actuales.includes(mes)
            ? actuales.filter(m => m !== mes)
            : [...actuales, mes];
        aplicarCambioAnio(anio, nuevosMeses);
    };

    const toggleAnioCompleto = (anio, marcar) => {
        const todosLosMeses = Array.from(mesesPorAnio.get(anio) || []);
        aplicarCambioAnio(anio, marcar ? todosLosMeses : []);
    };

    const handleLimpiar = () => onChange([]);

    const toggleExpandido = (anio) => {
        setAniosExpandido(prev => {
            const next = new Set(prev);
            if (next.has(anio)) next.delete(anio);
            else next.add(anio);
            return next;
        });
    };

    const totalSeleccionados = value.reduce((acc, v) => acc + v.meses.length, 0);

    return (
        <Dropdown autoClose={false}>
            <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                disabled={disabled || aniosDisponibles.length === 0}
            >
                <i className="bi bi-calendar3 me-1"></i>Última Fecha
                {totalSeleccionados > 0 && (
                    <Badge bg="primary" className="ms-2">{totalSeleccionados}</Badge>
                )}
            </Dropdown.Toggle>

            <Dropdown.Menu
                style={{ maxHeight: '360px', overflowY: 'auto', minWidth: '260px', padding: '0.5rem' }}
            >
                {aniosDisponibles.length === 0 ? (
                    <div className="px-3 py-2 text-muted small">No hay fechas disponibles</div>
                ) : (
                    <>
                        <div className="d-flex justify-content-between align-items-center px-2 pb-2 border-bottom mb-2">
                            <strong className="small">Filtrar por fecha</strong>
                            <Button
                                variant="link"
                                size="sm"
                                className="p-0"
                                onClick={handleLimpiar}
                                disabled={totalSeleccionados === 0}
                            >
                                Limpiar
                            </Button>
                        </div>

                        {aniosDisponibles.map(anio => {
                            const mesesDelAnio = Array.from(mesesPorAnio.get(anio)).sort((a, b) => a - b);
                            const seleccionados = mesesSeleccionadosDeAnio(anio);
                            return (
                                <div key={anio} className="mb-1">
                                    <div
                                        className="d-flex align-items-center px-2 py-1"
                                        style={{ cursor: 'pointer', backgroundColor: '#f8f9fa', borderRadius: '4px' }}
                                        onClick={() => toggleExpandido(anio)}
                                    >
                                        <i className={`bi bi-chevron-${estaExpandido(anio) ? 'down' : 'right'} me-2`}></i>
                                        <div onClick={(e) => e.stopPropagation()} className="d-flex align-items-center">
                                            <CheckboxAnio
                                                anio={anio}
                                                totalMeses={mesesDelAnio.length}
                                                seleccionados={seleccionados}
                                                onToggle={toggleAnioCompleto}
                                                disabled={disabled}
                                            />
                                        </div>
                                        <span className="ms-2 fw-semibold">{anio}</span>
                                        {seleccionados.length > 0 && (
                                            <Badge bg="secondary" className="ms-2">{seleccionados.length}</Badge>
                                        )}
                                    </div>

                                    {estaExpandido(anio) && (
                                        <div className="ps-4 pt-1">
                                            {mesesDelAnio.map(mes => (
                                                <div key={mes} className="form-check">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id={`fecha-${anio}-${mes}`}
                                                        checked={seleccionados.includes(mes)}
                                                        disabled={disabled}
                                                        onChange={() => toggleMes(anio, mes)}
                                                    />
                                                    <label
                                                        className="form-check-label small"
                                                        htmlFor={`fecha-${anio}-${mes}`}
                                                    >
                                                        {NOMBRES_MESES[mes]}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </>
                )}
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default FiltroFechaExcel;
